import React from "react";

import {
  compact,
  get,
  intersection,
  isEmpty,
  map,
  noop,
  toNumber,
  uniq,
} from "lodash/fp";
import { UseFieldArrayReturn } from "react-hook-form";

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { Stack, Typography } from "@mui/material";

import PurchaseLicensesAlert from "@app/analytics/account/Billing/components/PurchaseLicensesAlert";
import ActionButton from "@app/analytics/account/components/ActionButton";
import {
  DEFAULT_WORKSPACE_INVITE_INPUT,
  WORKSPACE_INVITE_FIELD_ARRAY_NAME,
} from "@app/analytics/account/WorkspaceSettings/hooks/useInviteWorkspaceMembersForm";
import {
  WorkspaceInviteForm,
  WorkspaceInviteFormReturn,
  WorkspaceInviteInputs,
} from "@app/analytics/account/WorkspaceSettings/WorkspaceWizard/types";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useAuth from "@app/common/auth/useAuth";
import { EnterpriseRole, EnterpriseTeam } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import Alert from "@rui/Alert";
import Button from "@rui/Button";
import { Row } from "@rui/Stack";

import InviteWorkspaceMemberInputs from "./InviteWorkspaceMemberInputs";

type FormValidator = (inputs: WorkspaceInviteInputs[]) => boolean;

const teamsToDropdownItems = (teams: Partial<EnterpriseTeam>[]) =>
  teams.map((team) => ({ value: team.name!, label: team.name! }));

interface InviteWorkspaceMembersFormProps {
  context: "wizard" | "modal";
  fieldArray: UseFieldArrayReturn<WorkspaceInviteForm>;
  form: WorkspaceInviteFormReturn;
  formId?: string;
  loading?: boolean;
  onSubmit: () => void;
  requireUsers?: boolean;
  teams: Partial<EnterpriseTeam>[];
}

const InviteWorkspaceMembersForm = ({
  context,
  fieldArray,
  form,
  formId,
  loading = false,
  onSubmit,
  requireUsers = false,
  teams,
}: InviteWorkspaceMembersFormProps) => {
  const { user } = useAuth();
  const { inWorkspace, workspace } = useWorkspace();
  const { licenses, subscription, subscriptionIsError } = useBillingContext();

  const newInviteLimit = licenses.numUnclaimed;

  const {
    clearErrors,
    formState: { errors: formErrors },
    getValues,
    handleSubmit,
    register,
    setValue,
    setError,
    watch,
  } = form;

  const handleAddInputs = () =>
    fieldArray.append(DEFAULT_WORKSPACE_INVITE_INPUT);
  const handleRemoveInput = (index: number) => () => fieldArray.remove(index);

  const validateAllEmailsHaveValue: FormValidator = (formValues) => {
    const effectiveFormValues = formValues.slice(0, newInviteLimit);
    const inputIndicesWithoutEmails: number[] = compact(
      effectiveFormValues.map((value, i) => (value.email ? null : `${i}`))
    ).map(toNumber); // coerce to string and back to number so that the compact doesn't filter out 0

    if (isEmpty(inputIndicesWithoutEmails)) return true;

    // This lets us skip adding emails in the workspace wizard if the user doesn't want to add any
    if (
      inputIndicesWithoutEmails.length === 1 &&
      inputIndicesWithoutEmails[0] === 0 &&
      !requireUsers
    ) {
      return true;
    }

    inputIndicesWithoutEmails.forEach((index) => {
      setError(`${WORKSPACE_INVITE_FIELD_ARRAY_NAME}.${index}.email`, {
        type: "custom",
        message: "Required",
      });
    });

    return false;
  };

  const validateAllEmailsCanBeInvited: FormValidator = (formValues) => {
    const effectiveFormValues = formValues.slice(0, newInviteLimit);
    const emails = uniq(
      compact(effectiveFormValues.map((value) => value.email))
    );

    if (inWorkspace) {
      const userEmailsInWorkspace = map("email", workspace.users);
      const duplicateEmails = intersection(emails, userEmailsInWorkspace);

      if (isEmpty(duplicateEmails)) return true;

      effectiveFormValues.forEach((value, i) => {
        if (duplicateEmails.includes(value.email)) {
          setError(`${WORKSPACE_INVITE_FIELD_ARRAY_NAME}.${i}.email`, {
            type: "custom",
            message: "User is already in this workspace",
          });
        }
      });

      return false;
    }

    const userAddingSelf = emails.includes(user!.email);
    if (userAddingSelf) {
      const index = formValues.findIndex(
        (value) => value.email === user!.email
      );
      setError(`${WORKSPACE_INVITE_FIELD_ARRAY_NAME}.${index}.email`, {
        type: "custom",
        message: "You are already in the workspace!",
      });

      return false;
    }

    return true;
  };

  const validateAllEmailsUnique: FormValidator = (formValues) => {
    const emails = formValues.map((value) => value.email);
    if (emails.length !== uniq(emails).length) {
      emails.forEach((email, i) => {
        if (emails.indexOf(email) !== i) {
          setError(`${WORKSPACE_INVITE_FIELD_ARRAY_NAME}.${i}.email`, {
            type: "custom",
            message: "Duplicate email",
          });
        }
      });
      return false;
    }

    return true;
  };

  const validateUserRoles: FormValidator = (formValues) => {
    const needsValidation = formValues.some(
      (input) => input.role === "manager"
    );

    if (needsValidation) {
      const inputIndicesWithIncompleteInfo = formValues
        .map((input, i) => {
          if (input.role === "manager") {
            return input.team ? -1 : i;
          }
          return -1;
        })
        .filter((index: number) => index > -1);

      if (!isEmpty(inputIndicesWithIncompleteInfo)) {
        inputIndicesWithIncompleteInfo.forEach((index: number) => {
          setError(`${WORKSPACE_INVITE_FIELD_ARRAY_NAME}.${index}.team`, {
            type: "custom",
            message: "Managers must be assigned a team",
          });
        });
        return false;
      }
    }

    return true;
  };

  const validateForm = () => {
    const formValues: WorkspaceInviteInputs[] = getValues(
      WORKSPACE_INVITE_FIELD_ARRAY_NAME
    );

    return [
      validateAllEmailsHaveValue,
      validateAllEmailsUnique,
      validateAllEmailsCanBeInvited,
      validateUserRoles,
    ]
      .map((validator) => validator(formValues))
      .every((isValid) => isValid);
  };

  const handleSubmitSuccess = () => {
    const isValid = validateForm();
    if (isValid) {
      onSubmit();
    }
  };

  // Add 1 because we want to show the add another button so when it's clicked,
  // the user sees the message that they must upgrade before adding additional users
  const showAddAnother = fieldArray.fields.length < newInviteLimit + 1;
  const fieldsToShow = subscriptionIsError
    ? []
    : fieldArray.fields.slice(0, newInviteLimit);
  const showPurchaseLicensesAlert = fieldArray.fields.length > newInviteLimit;

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(handleSubmitSuccess, validateForm)}
    >
      <Stack spacing={context === "wizard" ? 3 : 1}>
        <Stack
          sx={{
            padding: 0,
            margin: 0,
            ".MuiInputBase-root": {
              padding: 0,
            },
          }}
          spacing={1}
        >
          <Row sx={{ mt: 1 }} alignItems="center">
            <Typography variant="bodyPrimaryMed">
              {context === "wizard" ? "Add" : "Enter"} emails
            </Typography>
          </Row>
          {fieldsToShow.map((field, i) => {
            const emailFieldName =
              `${WORKSPACE_INVITE_FIELD_ARRAY_NAME}.${i}.email` as const;
            const roleFieldName =
              `${WORKSPACE_INVITE_FIELD_ARRAY_NAME}.${i}.role` as const;
            const teamFieldName =
              `${WORKSPACE_INVITE_FIELD_ARRAY_NAME}.${i}.team` as const;
            return (
              <InviteWorkspaceMemberInputs
                key={`invite-inputs-${i}`}
                allowRemove={i > 0}
                onRemove={handleRemoveInput(i)}
                email={{
                  ...register(emailFieldName),
                  onChange: (value: string) => {
                    clearErrors(emailFieldName);
                    setValue(emailFieldName, value);
                  },
                  error: get(`${emailFieldName}.message`, formErrors),
                }}
                role={{
                  ...register(roleFieldName),
                  onChange: (value: string) =>
                    setValue(roleFieldName, value as EnterpriseRole),
                  selected: watch(roleFieldName),
                }}
                team={{
                  ...register(teamFieldName),
                  onChange: (value: string) => {
                    clearErrors(teamFieldName);
                    setValue(teamFieldName, value);
                  },
                  selected: watch(teamFieldName),
                  error: get(`${teamFieldName}.message`, formErrors),
                }}
                teams={teamsToDropdownItems(teams)}
              />
            );
          })}
          {(showPurchaseLicensesAlert || subscriptionIsError) && (
            <>
              <InviteWorkspaceMemberInputs
                key="invite-inputs-dummy"
                allowRemove
                onRemove={
                  fieldsToShow.length === 0
                    ? undefined
                    : handleRemoveInput(fieldArray.fields.length - 1)
                }
                email={{
                  name: "email_disabled",
                  disabled: true,
                  onChange: noop,
                }}
                role={{
                  name: "role_disabled",
                  disabled: true,
                  onChange: noop,
                }}
                team={{
                  name: "team_disabled",
                  disabled: true,
                  onChange: noop,
                }}
                teams={[]}
              />
              {subscriptionIsError ? (
                <Alert severity="error" embedded>
                  <Typography
                    variant="bodySecondaryReg"
                    sx={{ lineHeight: "1.7" }}
                  >
                    Error loading available licenses. Please try again later.
                  </Typography>
                </Alert>
              ) : (
                <PurchaseLicensesAlert
                  embedded
                  inTrialPeriod={subscription.inTrialPeriod}
                  licenses={licenses}
                />
              )}
            </>
          )}
        </Stack>
        {showAddAnother && (
          <ActionButton
            sx={{ width: "fit-content" }}
            icon={<AddCircleOutlineOutlinedIcon sx={{ width: "18px" }} />}
            onClick={handleAddInputs}
          >
            <Typography variant="button" sx={{ ml: 1 }}>
              Add another
            </Typography>
          </ActionButton>
        )}
        {context === "wizard" && (
          <Row spacing={2}>
            <Button type="submit" loading={loading} sx={{ width: "160px" }}>
              Send invites
            </Button>
            <Button variant="text" onClick={onSubmit}>
              Skip for now
            </Button>
          </Row>
        )}
      </Stack>
    </form>
  );
};

export default InviteWorkspaceMembersForm;
